import React, { useEffect, useRef, useState, useCallback } from "react";
import { connect } from "react-redux";
import { Popup } from "devextreme-react/popup";
import {
    DataGrid,
    Column,
    Editing,
    Lookup,
    Scrolling,
    LoadPanel as GridLoadPanel,
} from "devextreme-react/data-grid";
import { Button } from "devextreme-react";
import { alert, confirm } from "devextreme/ui/dialog";
import notify from "devextreme/ui/notify";
import axios from "axios";
import routes from "../../../../../assets/apiRoutes.json";
import { MESSAGES } from "../../../../../common/Messages";
import FormField from "../../../../../dynamicFormBuilder/FormField";
import { CodeLookupLayout } from "../../../../../common/lookups/Layouts/ImLayouts";

const SERIAL_NUMBER_SHIPMENT_LAYOUT_ID = "E2A6C815-9F47-4B3D-8A61-C0D5B4F7E982";

const yesNoItems = [
    { ID: "Y", Text: "Yes" },
    { ID: "N", Text: "No" },
];

/* Legacy CalcEdit columns show 2 decimals; the editor entry is masked to 2 decimals too. */
const numberFormat = { type: "fixedPoint", precision: 2 };
const numberEditorOptions = { format: numberFormat };

const initialFormFields = {
    AutoSelectQuantity: null,
    TotalNetWeightSelected: 0,
    Reference2: null,
    ShipmentVerified: null,
    Code1: null,
    Code2: null,
};
const buildDefaultFormSchema = (isCommitted) => [
    {
        groupId: 1,
        label: "Auto Select",
        fields: [
            { name: "AutoSelectQuantity", tableName: "SerialNumberShipment", label: "Auto Select Quantity", type: "number", required: false, visible: true, readOnly: isCommitted },
            { name: "TotalNetWeightSelected", tableName: "SerialNumberShipment", label: "Total Net Weight Selected", type: "number", required: false, visible: true, readOnly: true, format: { type: "fixedPoint", precision: 2 } },
        ],
    },
    {
        groupId: 2,
        label: "Default Values",
        fields: [
            { name: "Reference2", tableName: "SerialNumberShipment", label: "Reference 2", type: "text", required: false, visible: true, readOnly: isCommitted, maxLength: 50 },
            { name: "ShipmentVerified", tableName: "SerialNumberShipment", label: "Shipment Verified", type: "select", required: false, visible: true, readOnly: isCommitted },
        ],
    },
    {
        groupId: 3,
        label: "Default Values - Codes",
        fields: [
            { name: "Code1", tableName: "SerialNumberShipment", label: "Code 1",layout:CodeLookupLayout({codeType:'B4'}), type: "lookup", required: false, visible: true, readOnly: isCommitted, showClearButton: true },
            { name: "Code2", tableName: "SerialNumberShipment", label: "Code 2",layout:CodeLookupLayout({codeType:'B5'}), type: "lookup", required: false, visible: true, readOnly: isCommitted, showClearButton: true },
        ],
    },
];

function SerialNumberShipmentPopup(props) {
    const { showPopup = {}, setShowPopup, oidc, onSuccess } = props;
    const visible = showPopup.visible === true;
    const detailRowId = showPopup.detailRowId;
    const currentUser = oidc?.user;
    const accessToken = currentUser?.access_token;
    const DYNAMICFORMSETTING = currentUser?.profile?.DYNAMICFORMSETTING;

    const dataGridRef = useRef(null);
    const formRefs = useRef({});
    const rowsRef = useRef([]);
    const [shipmentData, setShipmentData] = useState(null);
    const [rows, setRows] = useState([]);
    const [formFields, setformFields] = useState(initialFormFields);
    const [saving, setSaving] = useState(false);
    const isCommitted = shipmentData?.IsCommitted === true;

    const closePopup = useCallback(() => {
        setShipmentData(null);
        setRows([]);
        rowsRef.current = [];
        formRefs.current = {};
        setformFields(initialFormFields);
        setShowPopup((prev) => ({ ...prev, visible: false }));
    }, [setShowPopup]);

    const computeTotalSelectedNetWeight = useCallback((list) => {
        const total = (list || []).reduce(
            (sum, row) => (row.Selected ? sum + (Number(row.NetWeight) || 0) : sum),
            0
        );
        setformFields((prev) => ({ ...prev, TotalNetWeightSelected: total }));
    }, []);

    useEffect(() => {
        if (!visible || !detailRowId || !accessToken) return;
        let cancelled = false;
        const headers = { Authorization: `Bearer ${accessToken}` };
        axios
            .get(`${window._env_.REACT_APP_APIS}/${routes.OM.GetSerialNumberShipmentData}`, {
                params: { detailRowId },
                headers,
            })
            .then(async (res) => {
                if (cancelled) return;
                const payload = res.data || {};
                if (payload.Success === false || !payload.Resultdata) {
                    alert(payload.Message || MESSAGES.ERROR, MESSAGES.ERROR).then(closePopup);
                    return;
                }
                const data = payload.Resultdata;
                setShipmentData(data);
                const listRes = await axios.get(
                    `${window._env_.REACT_APP_APIS}/${routes.OM.GetShipmentSerialNumbers}`,
                    { params: { detailRowId }, headers }
                );
                if (cancelled) return;
                /* DataSourceLoader result may arrive as {data:[...]} or as a plain array. */
                const list = Array.isArray(listRes.data) ? listRes.data : listRes.data?.data || [];
                rowsRef.current = list;
                setRows(list);
                computeTotalSelectedNetWeight(list);
            })
            .catch((err) => {
                if (cancelled) return;
                const msg = err.response?.data?.message || err.message;
                alert(typeof msg === "string" ? msg : JSON.stringify(msg), MESSAGES.ERROR).then(closePopup);
            });
        return () => {
            cancelled = true;
        };
    }, [visible, detailRowId, accessToken, closePopup, computeTotalSelectedNetWeight]);

    const handleFieldValueChange = useCallback(
        (name, value, e) => {
            if (name !== "AutoSelectQuantity" || !e?.event) return;
            const quantity = Number(value) || 0;
            if (quantity <= 0) return;
            const grid = dataGridRef.current?.instance();
            if (grid) grid.cancelEditData();
            /* Selection follows serial-number order (legacy sort), grid order stays as loaded. */
            const selectedIds = new Set(
                [...rowsRef.current]
                    .sort((a, b) => String(a.SerialNumber ?? "").localeCompare(String(b.SerialNumber ?? "")))
                    .slice(0, quantity)
                    .map((row) => row.RowId)
            );
            const updated = rowsRef.current.map((row) => ({ ...row, Selected: selectedIds.has(row.RowId) }));
            rowsRef.current = updated;
            setRows(updated);
            computeTotalSelectedNetWeight(updated);
        },
        [computeTotalSelectedNetWeight]
    );

    const onRowUpdated = useCallback(() => {
        computeTotalSelectedNetWeight(rowsRef.current);
    }, [computeTotalSelectedNetWeight]);

    /* Legacy acceptButton_Click / SaveSerialNumbers: apply Selected flags + non-blank
       defaults to every listed serial in one call, then close. */
    const handleAccept = useCallback(async () => {
        if (isCommitted) {
            closePopup();
            return;
        }
        const grid = dataGridRef.current?.instance();
        if (grid) await grid.saveEditData();
        setSaving(true);
        try {
            /* Legacy CopyNonBlankValues: the non-blank form defaults overwrite the
               values of SELECTED serials before they are saved. */
            const isBlank = (v) => v == null || v === "";
            const rows = rowsRef.current.map((row) => {
                const selected = row.Selected === true;
                return {
                    RowId: row.RowId,
                    Selected: selected,
                    SerialNumber: row.SerialNumber,
                    NetWeight: row.NetWeight,
                    GrossWeight: row.GrossWeight,
                    Size1: row.Size1,
                    Size2: row.Size2,
                    Code1: selected && !isBlank(formFields.Code1) ? formFields.Code1 : row.Code1,
                    Code2: selected && !isBlank(formFields.Code2) ? formFields.Code2 : row.Code2,
                    Code3: row.Code3,
                    Reference1: row.Reference1,
                    Reference2: selected && !isBlank(formFields.Reference2) ? formFields.Reference2 : row.Reference2,
                    Reference3: row.Reference3,
                    Reference4: row.Reference4,
                    Reference5: row.Reference5,
                    ShipmentVerified:
                        selected && !isBlank(formFields.ShipmentVerified)
                            ? formFields.ShipmentVerified
                            : row.ShipmentVerified,
                    DamageFlag: row.DamageFlag,
                    DamageDescription: row.DamageDescription,
                    Comment: row.Comment,
                };
            });
            const res = await axios.post(
                `${window._env_.REACT_APP_APIS}/${routes.OM.UpdateSerialNumbers}`,
                rows,
                {
                    params: { detailRowId },
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${accessToken}`,
                    },
                }
            );
            const payload = res.data || {};
            if (payload.Success === false) {
                alert(payload.Message || MESSAGES.ERROR, MESSAGES.ERROR);
                return;
            }
            notify({ message: "Serial Numbers applied.", width: 250 }, "success", 3000);
            if (onSuccess) onSuccess();
            closePopup();
        } catch (err) {
            const msg = err.response?.data?.message || err.message;
            alert(typeof msg === "string" ? msg : JSON.stringify(msg), MESSAGES.ERROR);
        } finally {
            setSaving(false);
        }
    }, [isCommitted, detailRowId, formFields, accessToken, onSuccess, closePopup]);

    /* Legacy cancelButton_Click: warn that in-memory changes will be discarded. */
    const handleCancel = useCallback(() => {
        if (rowsRef.current.length > 0 && !isCommitted) {
            confirm(
                "<i>Any changes that have been made will not be applied to the database.  Do you wish to continue?</i>",
                "Changes Not Saved"
            ).then((dialogResult) => {
                if (dialogResult) closePopup();
            });
            return;
        }
        closePopup();
    }, [isCommitted, closePopup]);

    const handleHiding = useCallback(
        (e) => {
            e.cancel = true;
            handleCancel();
        },
        [handleCancel]
    );
    const loadSelectOptions = useCallback(() => {
        const verifiedRef = formRefs.current["ShipmentVerified"];
        /* instance() returns undefined while the widget is not yet created or already
           disposed (stale ref from a previous popup open) — never call option() on it. */
        const instance = verifiedRef?.instance ? verifiedRef.instance() : null;
        if (!instance) return;
        instance.option("dataSource", yesNoItems);
        instance.option("valueExpr", "ID");
        instance.option("displayExpr", "Text");
    }, []);

    const handleFormLoad = useCallback(() => {
        setTimeout(() => {
            loadSelectOptions();
        }, 100);
    }, [loadSelectOptions]);

    useEffect(() => {
        loadSelectOptions();
    }, [loadSelectOptions, shipmentData]);
    
    if (!visible) return null;

    return (
        <Popup
            visible={true}
            onHiding={handleHiding}
            dragEnabled={true}
            hideOnOutsideClick={false}
            showCloseButton={true}
            showTitle={true}
            title={showPopup.title ?? "Serial Number Shipment"}
            container="#root"
            width="85vw"
            height="90vh"
        >
            <div style={{ height: "100%", display: "flex", flexDirection: "column", padding: "4px 8px" }}>
                <div style={{ flex: "1 1 auto", overflowY: "auto", minHeight: 0 }}>
                    {shipmentData && (
                        <FormField
                            currrentUser={currentUser}
                            layoutId={SERIAL_NUMBER_SHIPMENT_LAYOUT_ID}
                            layoutName="OMSerialNumberShipmentForm"
                            defaultFormSchema={buildDefaultFormSchema(isCommitted)}
                            formFields={formFields}
                            setformFields={setformFields}
                            handleFieldValueChange={handleFieldValueChange}
                            DYNAMICFORMSETTING={DYNAMICFORMSETTING}
                            formRefs={formRefs}
                            onFormLoad={handleFormLoad}
                            noSpacingBeforeFirstRow={true}
                        />
                    )}
                    <div style={{ marginTop: 8 }}>
                        <DataGrid
                            ref={dataGridRef}
                            dataSource={rows}
                            keyExpr="RowId"
                            showBorders={true}
                            columnAutoWidth={true}
                            allowColumnResizing={true}
                            allowColumnReordering={true}
                            rowAlternationEnabled={true}
                            height="42vh"
                            onRowUpdated={onRowUpdated}
                        >
                            <Scrolling mode="standard" />
                            <GridLoadPanel enabled={true} />
                            <Editing mode="cell" allowUpdating={!isCommitted} useIcons={true} />
                            <Column dataField="RowId" visible={false} allowEditing={false} />
                            <Column dataField="Selected" caption="Selected" dataType="boolean" />
                            <Column dataField="SerialNumber" caption="Serial Number" allowEditing={false} />
                            <Column dataField="NetWeight" caption="Net Weight" dataType="number" format={numberFormat} editorOptions={numberEditorOptions} />
                            <Column dataField="GrossWeight" caption="Gross Weight" dataType="number" format={numberFormat} editorOptions={numberEditorOptions} />
                            <Column dataField="Size1" caption="Size 1" dataType="number" format={numberFormat} editorOptions={numberEditorOptions} />
                            <Column dataField="Size2" caption="Size 2" dataType="number" format={numberFormat} editorOptions={numberEditorOptions} />
                            <Column dataField="Reference2" caption="Reference 2" />
                            <Column dataField="DamageFlag" caption="Damage Flag">
                                <Lookup dataSource={yesNoItems} valueExpr="ID" displayExpr="Text" />
                            </Column>
                            <Column dataField="DamageDescription" caption="Damage Description" />
                        </DataGrid>
                    </div>
                </div>
                <div
                    style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 10,
                        paddingTop: 10,
                        borderTop: "1px solid #e0e0e0",
                        flexShrink: 0,
                    }}
                >
                    <Button
                        text="Accept"
                        type="default"
                        stylingMode="contained"
                        disabled={saving || !shipmentData}
                        onClick={handleAccept}
                    />
                    <Button text="Cancel" disabled={saving} onClick={handleCancel} />
                </div>
            </div>
        </Popup>
    );
}

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}

export default connect(mapStateToProps)(SerialNumberShipmentPopup);
